import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mockPrograms, mockCategories } from "@/mocks/programs";

export default function ProgramsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("popular");
  const [priceRange, setPriceRange] = useState("all");
  const [selectedProgram, setSelectedProgram] = useState<typeof mockPrograms[0] | null>(null);
  const [purchaseModal, setPurchaseModal] = useState(false);
  const [purchased, setPurchased] = useState<string[]>([]);

  const filtered = mockPrograms.filter((p) => {
    const matchCat = activeCategory === "all" || p.category.toLowerCase() === activeCategory;
    const matchPrice =
      priceRange === "all" ||
      (priceRange === "under100" && p.price < 100) ||
      (priceRange === "100to200" && p.price >= 100 && p.price <= 200) ||
      (priceRange === "over200" && p.price > 200);
    return matchCat && matchPrice;
  });

  const handlePurchase = (prog: typeof mockPrograms[0]) => {
    setSelectedProgram(prog);
    setPurchaseModal(true);
  };

  const confirmPurchase = () => {
    if (selectedProgram) {
      setPurchased((prev) => [...prev, selectedProgram.id]);
    }
    setPurchaseModal(false);
    setSelectedProgram(null);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a90401b6b2fc56db157df85cfb488008.jpg"
            alt="Programs"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 to-black/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Programs Marketplace
          </span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Invest in Your Growth
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            World-class coaching programs in mindset, life, career, corporate success, and wellness — crafted by expert coaches.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Category Tabs */}
          <div className="flex gap-2 flex-wrap">
            {mockCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.id
                    ? "bg-teal-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Sort & Price */}
          <div className="flex gap-3">
            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-teal-400 cursor-pointer bg-white"
            >
              <option value="all">All Prices</option>
              <option value="under100">Under $100</option>
              <option value="100to200">$100 – $200</option>
              <option value="over200">Over $200</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-teal-400 cursor-pointer bg-white"
            >
              <option value="popular">Most Popular</option>
              <option value="rating">Top Rated</option>
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-sm text-gray-500 mb-6">
            Showing <strong className="text-gray-900">{filtered.length}</strong> programs
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((program) => (
              <div
                key={program.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-teal-200 transition-all group"
              >
                {/* Cover */}
                <div className="relative h-48 overflow-hidden cursor-pointer" onClick={() => setSelectedProgram(program)}>
                  <img
                    src={program.cover}
                    alt={program.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {program.tag}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="bg-white/90 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                      {program.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 leading-snug">{program.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{program.description}</p>

                  {/* Coach */}
                  <div className="flex items-center gap-2 mb-4">
                    <img src={program.coachAvatar} alt={program.coach} className="w-7 h-7 rounded-full object-cover object-top" />
                    <span className="text-gray-600 text-xs font-medium">{program.coach}</span>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-4 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400" />
                      <strong className="text-gray-700">{program.rating}</strong> ({program.reviews})
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {program.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-book-open-line" />
                      {program.lessons} lessons
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-bar-chart-line" />
                      {program.level}
                    </span>
                  </div>

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-gray-900">${program.price}</span>
                    {purchased.includes(program.id) ? (
                      <button className="px-5 py-2 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-xl cursor-default whitespace-nowrap">
                        <i className="ri-check-line mr-1" />
                        Enrolled
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePurchase(program)}
                        className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Enroll Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      {purchaseModal && selectedProgram && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setPurchaseModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-shopping-cart-line text-teal-600 text-2xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Enroll in Program
              </h2>
              <p className="text-gray-500 text-sm">{selectedProgram.title}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Program Price</span>
                <span className="font-semibold text-gray-900">${selectedProgram.price}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Platform Fee</span>
                <span className="font-semibold text-gray-900">$0</span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-teal-600 text-lg">${selectedProgram.price}</span>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Card Number</label>
                <input
                  placeholder="4242 4242 4242 4242"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Expiry</label>
                  <input
                    placeholder="MM / YY"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">CVV</label>
                  <input
                    placeholder="123"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPurchaseModal(false)}
                className="flex-1 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmPurchase}
                className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Pay ${selectedProgram.price}
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-3">
              <i className="ri-shield-check-line mr-1 text-teal-500" />
              Secured by 256-bit SSL encryption
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
